import type { MbbBundle } from "../ShoppingCart/shop-types";

export type Badge = {
  text: string;
  cssClass: string;
};

export type DataPackage = {
  gb: number;
  name: string;
  subtext: string | null;
  peopleText: string;
  streamText: string;
  price: number;
  priceNoCampaign: number;
  subscriptionOfferingId: string;
  badges: Badge[];
  bindingPeriodInMonths: number;
  routerPrice: number;
  routerCampaignText?: string | null;
  routerPriceBefore?: number | null;
  routerCssClass?: string;
  bundle: MbbBundle;
};

const isValidPackage = (item: unknown): item is DataPackage => {
  if (typeof item !== "object" || item === null) return false;
  const pkg = item as Record<string, unknown>;

  return (
    typeof pkg.gb === "number" &&
    pkg.gb > 0 &&
    typeof pkg.name === "string" &&
    typeof pkg.price === "number" &&
    typeof pkg.bundle === "object" &&
    pkg.bundle !== null
  );
};

export const parsePackages = (json: string | undefined): DataPackage[] => {
  if (!json) return [];

  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(isValidPackage)
      .map((pkg) => ({
        ...pkg,
        badges: Array.isArray(pkg.badges) ? pkg.badges : [],
        bindingPeriodInMonths: typeof pkg.bindingPeriodInMonths === "number" ? pkg.bindingPeriodInMonths : 0,
      }))
      .sort((a, b) => a.gb - b.gb);
  } catch {
    return [];
  }
};

export const initialPackageIndex = (packages: DataPackage[]): number => {
  const badgeIndex = packages.findIndex((pkg) => pkg.badges.length > 0);
  return badgeIndex === -1 ? Math.floor(packages.length / 2) : badgeIndex;
};

export const formatGb = (gb: number): string => gb.toLocaleString("nb-NO").replace(/[\u00A0\u202F]/g, " ");

export const hasRouterPriceBefore = (pkg: DataPackage): boolean =>
  typeof pkg.routerPriceBefore === "number" && pkg.routerPriceBefore > pkg.routerPrice;

const buildRouterText = (pkg: DataPackage): string => {
  const campaign = pkg.routerCampaignText ? `${pkg.routerCampaignText}: ` : "";
  const priceBefore = hasRouterPriceBefore(pkg) ? `, førpris ${pkg.routerPriceBefore} kr` : "";
  return `${campaign}ruter ${pkg.routerPrice} kr${priceBefore}`;
};

export const buildValueText = (pkg: DataPackage): string =>
  `${formatGb(pkg.gb)} GB${pkg.subtext ? ` – ${pkg.subtext}` : ""}, ${pkg.price} kr/mnd, ${buildRouterText(pkg)}`;

export const hasCampaignPrice = (pkg: DataPackage): boolean =>
  pkg.priceNoCampaign > 0 && pkg.priceNoCampaign !== pkg.price;
